(() => {
  const CONFIG = Object.assign(
    {
      enabled: false,
      apiBase: 'https://joix.search.zetacx.net/api/df',
      legacyHost: 'https://www.duffer.jp',
      perPage: 24,
      suggestEnabled: true,
      searchUrl: '/search',
      moneyFormat: '¥{{amount_no_decimals_with_comma_separator}}',
    },
    window.ZetaConfig || {},
  );

  const FACET_TYPES = [
    'brand',
    'category',
    'size',
    'fit',
    'color',
    'pattern',
    'style',
    'collection',
    'price',
    'type',
    'item_stock',
    'material',
  ];

  // 税込レンジをZETAのファセット名と同じ値で定義する。
  const PRICE_RANGES = [
    { min: 0, max: 4999 },
    { min: 5000, max: 9999 },
    { min: 10000, max: 19999 },
    { min: 20000, max: 29999 },
    { min: 30000, max: 39999 },
    { min: 40000, max: 49999 },
    { min: 50000, max: 99999 },
    { min: 100000, max: null },
  ];

  const PRODUCT_FETCH_CONCURRENCY = 6;
  const FALLBACK_IMAGE_WIDTH = 1000;
  const COLOR_OPTION_NAME_KEYWORDS = ['カラー', 'color', '色'];

  const COLOR_SWATCH_MAP = {
    'ホワイト系': '#ffffff',
    'グレー系': '#d8d8d8',
    'ブラック系': '#000000',
    'ブラウン系': '#8f7a66',
    'ベージュ系': '#b8b09c',
    'グリーン系': '#799995',
    'ブルー系': '#7c8ba6',
    'パープル系': '#867999',
    'レッド系': '#db0000',
    'オレンジ系': '#d69a6a',
    'ピンク系': '#b89c9c',
    'イエロー系': '#d9c979',
  };

  function formatMoney(yen) {
    if (yen == null || String(yen).trim() === '') {
      return '';
    }
    const amount = Number(yen);
    if (!Number.isFinite(amount)) {
      return '';
    }
    return `¥${amount.toLocaleString('ja-JP')}`;
  }

  async function fetchShopifyProductByHandle(handle) {
    try {
      const response = await fetch(`/products/${encodeURIComponent(handle)}.js`);
      if (response.ok) {
        return await response.json();
      }
      return null;
    } catch {
      return null;
    }
  }

  // 18文字固定IDをそのまま引けなければ、末尾のパディングを外した品番でもう一度試す。
  async function fetchShopifyProduct(itemId) {
    const handle = String(itemId).toLowerCase();
    const product = await fetchShopifyProductByHandle(handle);
    if (product) {
      return product;
    }
    const trimmedHandle = handle.replace(/_+$/, '');
    if (trimmedHandle === '' || trimmedHandle === handle) {
      return null;
    }
    return await fetchShopifyProductByHandle(trimmedHandle);
  }

  function resolveVariantId(product, wishlistTarget) {
    const variants = Array.isArray(product?.variants) ? product.variants : [];
    const targetSku = String(wishlistTarget?.sku || '');

    // Shopify側のSKU規則が未検証のため、最も確実なZETA SKUの完全一致を優先する。
    if (targetSku) {
      const exactSkuVariant = variants.find((variant) => variant.sku === targetSku);
      if (exactSkuVariant) {
        return exactSkuVariant.id;
      }
    }

    const itemId = String(wishlistTarget?.itemId || '');
    const colorCode = String(wishlistTarget?.colorCode || '');
    // 完全一致しない場合も同じ色の別サイズを拾えるよう、商品IDと色コードのSKU接頭辞で照合する。
    if (itemId && colorCode) {
      const skuPrefix = `${itemId}${colorCode}`;
      const colorSkuVariant = variants.find((variant) => String(variant.sku || '').startsWith(skuPrefix));
      if (colorSkuVariant) {
        return colorSkuVariant.id;
      }
    }

    const colorName = String(wishlistTarget?.colorName || '').trim();
    const productOptions = Array.isArray(product?.options) ? product.options : [];
    // SKU規則で特定できない場合に備え、Shopifyの商品オプション名とZETAカラー名でも照合する。
    if (colorName) {
      const colorOptionIndex = productOptions.findIndex((option) => {
        const optionName = typeof option === 'string' ? option : option?.name;
        const normalizedOptionName = String(optionName || '').trim().toLowerCase();
        return COLOR_OPTION_NAME_KEYWORDS.some((keyword) => normalizedOptionName.includes(keyword));
      });

      if (colorOptionIndex >= 0) {
        const colorOption = productOptions[colorOptionIndex];
        const configuredPosition = typeof colorOption === 'object' ? Number(colorOption?.position) : 0;
        const optionPosition = configuredPosition >= 1 && configuredPosition <= 3 ? configuredPosition : colorOptionIndex + 1;
        const optionProperty = `option${optionPosition}`;
        const colorOptionVariant = variants.find((variant) => String(variant[optionProperty] || '').trim() === colorName);
        if (colorOptionVariant) {
          return colorOptionVariant.id;
        }
      }
    }

    return product?.variants?.[0]?.id ?? null;
  }

  // ZETAのカラー情報に対応するShopifyバリアントを全サイズ分返す。
  function resolveColorVariants(product, wishlistTarget) {
    const variants = Array.isArray(product?.variants) ? product.variants : [];
    const itemId = String(wishlistTarget?.itemId || '');
    const colorCode = String(wishlistTarget?.colorCode || '');

    if (itemId && colorCode) {
      const skuPrefix = `${itemId}${colorCode}`;
      const colorSkuVariants = variants.filter((variant) => String(variant.sku || '').startsWith(skuPrefix));
      if (colorSkuVariants.length > 0) {
        return colorSkuVariants;
      }
    }

    const colorName = String(wishlistTarget?.colorName || '').trim();
    const productOptions = Array.isArray(product?.options) ? product.options : [];
    if (colorName) {
      const colorOptionIndex = productOptions.findIndex((option) => {
        const optionName = typeof option === 'string' ? option : option?.name;
        const normalizedOptionName = String(optionName || '').trim().toLowerCase();
        return COLOR_OPTION_NAME_KEYWORDS.some((keyword) => normalizedOptionName.includes(keyword));
      });

      if (colorOptionIndex >= 0) {
        const colorOption = productOptions[colorOptionIndex];
        const configuredPosition = typeof colorOption === 'object' ? Number(colorOption?.position) : 0;
        const optionPosition = configuredPosition >= 1 && configuredPosition <= 3 ? configuredPosition : colorOptionIndex + 1;
        const optionProperty = `option${optionPosition}`;
        return variants.filter((variant) => String(variant[optionProperty] || '').trim() === colorName);
      }
    }

    return [];
  }

  // Shopify画像URLへカード表示用の幅パラメータを付ける。
  function withImageWidth(url) {
    if (typeof url !== 'string' || url.trim() === '') {
      return '';
    }
    try {
      const imageUrl = new URL(url, window.location.href);
      imageUrl.searchParams.set('width', String(FALLBACK_IMAGE_WIDTH));
      return imageUrl.toString();
    } catch {
      return '';
    }
  }

  // Shopifyの重複アップロードUUIDを除き、画像のカット識別子を返す。
  function resolveImageCutKey(url) {
    if (!url) {
      return '';
    }

    let pathname;
    try {
      pathname = new URL(url, window.location.href).pathname;
    } catch {
      pathname = String(url);
    }

    const filename = pathname.split('/').pop() || '';
    return filename
      .replace(/\.[^./]+$/, '')
      .replace(/_[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i, '');
  }

  // ホバー画像の再開時に使うため、異なるカットを選ぶ処理は一時的に残す。
  function findDifferentCutImageUrl(imageUrls, baseImageUrl) {
    if (!Array.isArray(imageUrls)) {
      return '';
    }

    const baseImageCutKey = resolveImageCutKey(baseImageUrl);
    return imageUrls.find((imageUrl) => imageUrl && resolveImageCutKey(imageUrl) !== baseImageCutKey) || '';
  }

  // ZETAのカラー情報に対応するShopifyの主画像を返す。
  function resolveShopifyImageUrls(product, wishlistTarget) {
    const colorVariants = resolveColorVariants(product, wishlistTarget);
    const variantWithImage = colorVariants.find((variant) => variant.featured_image?.src);
    const primaryImageUrl = variantWithImage?.featured_image?.src || product?.featured_image || product?.images?.[0] || '';
    return {
      primary: withImageWidth(primaryImageUrl),
      hover: '',
    };
  }

  // 画像URL未設定または読み込み失敗済みかを判定する。
  function isImageBroken(img) {
    return !img.getAttribute('src') || (img.complete && img.naturalWidth === 0);
  }

  // 壊れた画像へShopify画像を一度だけ適用する。
  function applyImageFallback(img, url, alt) {
    if (img.dataset.zetaImageFallback === '1') {
      return;
    }
    if (!url) {
      img.remove();
      return;
    }
    img.dataset.zetaImageFallback = '1';
    img.src = url;
    if (alt && !img.alt) {
      img.alt = alt;
    }
  }

  // 現在と遅延読み込み後の両方で画像フォールバックを適用する。
  function prepareImageFallback(img, url, alt = '') {
    if (!img) {
      return;
    }
    if (isImageBroken(img)) {
      applyImageFallback(img, url, alt);
      return;
    }
    img.addEventListener('error', () => applyImageFallback(img, url, alt), { once: true });
  }

  // 主画像の破損時に主画像とホバー画像をShopify画像のペアへ揃える。
  function prepareImagePairFallback(primaryImage, hoverImage, imageUrls, alt = '') {
    const applyImagePairFallback = () => {
      if (primaryImage) {
        applyImageFallback(primaryImage, imageUrls.primary, alt);
      }
      if (hoverImage) {
        applyImageFallback(hoverImage, imageUrls.hover);
      }
    };

    if (!primaryImage) {
      prepareImageFallback(hoverImage, imageUrls.hover);
      return;
    }
    if (isImageBroken(primaryImage)) {
      applyImagePairFallback();
      return;
    }

    primaryImage.addEventListener('error', applyImagePairFallback, { once: true });
    prepareImageFallback(hoverImage, imageUrls.hover);
  }

  // 壊れたZETA商品画像だけをShopify商品JSONの画像で修復する。
  async function repairProductImages(container, options = {}) {
    const { imageSelector = '[data-zeta-image]' } = options;
    const images = Array.from(container.querySelectorAll(imageSelector)).filter(
      (img) => img.tagName === 'IMG' && img.dataset.zetaItemId && img.dataset.zetaImageRepair !== '1',
    );
    const brokenImages = [];

    const repairImage = async (img) => {
      const itemId = img.dataset.zetaItemId;
      const product = await fetchShopifyProduct(itemId);
      if (!product) {
        img.remove();
        return;
      }
      const imageUrls = resolveShopifyImageUrls(product, {
        itemId,
        sku: img.dataset.zetaSku,
        colorCode: img.dataset.zetaColorCode,
        colorName: img.dataset.zetaColorName,
      });
      applyImageFallback(img, imageUrls.primary, img.dataset.zetaImageAlt || '');
    };

    images.forEach((img) => {
      img.dataset.zetaImageRepair = '1';
      if (isImageBroken(img)) {
        brokenImages.push(img);
        return;
      }
      img.addEventListener('error', async () => await repairImage(img), { once: true });
    });

    for (let index = 0; index < brokenImages.length; index += PRODUCT_FETCH_CONCURRENCY) {
      const imageChunk = brokenImages.slice(index, index + PRODUCT_FETCH_CONCURRENCY);
      await Promise.all(imageChunk.map(repairImage));
    }
  }

  async function hydrateProductCards(container, options = {}) {
    const {
      className = 'c-product-card__wishlist',
      soldSelector,
      imageSelector = '[data-zeta-image]',
      hoverImageSelector = '[data-zeta-image-hover]',
    } = options;
    const slots = Array.from(container.querySelectorAll('[data-zeta-wishlist-slot][data-zeta-item-id]')).filter(
      (slot) => !slot.querySelector('wishlist-button-collection'),
    );

    for (let index = 0; index < slots.length; index += PRODUCT_FETCH_CONCURRENCY) {
      const slotChunk = slots.slice(index, index + PRODUCT_FETCH_CONCURRENCY);
      await Promise.all(
        slotChunk.map(async (slot) => {
          const wishlistTarget = {
            itemId: slot.dataset.zetaItemId,
            sku: slot.dataset.zetaSku,
            colorCode: slot.dataset.zetaColorCode,
            colorName: slot.dataset.zetaColorName,
          };
          const product = await fetchShopifyProduct(wishlistTarget.itemId);
          if (!slot.isConnected) {
            return;
          }

          const image = slot.querySelector(imageSelector);
          const hoverImage = hoverImageSelector ? slot.querySelector(hoverImageSelector) : null;
          if (product) {
            const imageUrls = resolveShopifyImageUrls(product, wishlistTarget);
            const colorName = String(slot.dataset.zetaColorName || '').trim();
            const imageAlt = colorName ? `${product.title} ${colorName}` : product.title;
            prepareImagePairFallback(image, hoverImage, imageUrls, imageAlt);
          } else {
            if (image && !image.getAttribute('src')) {
              image.remove();
            }
            if (hoverImage && !hoverImage.getAttribute('src')) {
              hoverImage.remove();
            }
          }

          if (soldSelector) {
            const soldElement = slot.querySelector(soldSelector);
            if (soldElement) {
              if (product) {
                const colorVariants = resolveColorVariants(product, wishlistTarget);
                const isAvailable =
                  colorVariants.length > 0
                    ? colorVariants.some((variant) => variant.available === true)
                    : product.available === true;
                soldElement.hidden = isAvailable;
              } else {
                soldElement.hidden = slot.dataset.zetaSoldOut !== '1';
              }
            }
          }

          if (!product || slot.querySelector('wishlist-button-collection')) {
            return;
          }

          const variantId = resolveVariantId(product, wishlistTarget);
          const wishlistButton = document.createElement('wishlist-button-collection');
          wishlistButton.className = className;
          wishlistButton.setAttribute('data-product-handle', product.handle);
          if (variantId != null) {
            wishlistButton.setAttribute('data-variant-id', String(variantId));
          }
          wishlistButton.setAttribute('show-icon', '');
          wishlistButton.setAttribute('icon', 'wishlist');
          slot.appendChild(wishlistButton);
        }),
      );
    }
  }

  // 税込レンジをZETAのファセット名と同じ文字列へ変換する。
  function getPriceRangeFacetName(range) {
    return `${range.min}-${range.max == null ? '' : range.max}`;
  }

  // ZETAのファセット名に対応する税込レンジ定義を返す。
  function resolvePriceRange(facetName) {
    return PRICE_RANGES.find((range) => getPriceRangeFacetName(range) === String(facetName || '')) || null;
  }

  // 税込レンジを画面表示用の価格ラベルへ整形する。
  function formatPriceRange(range) {
    if (!range) {
      return '';
    }
    if (range.min === 0) {
      return `〜${formatMoney(range.max)}`;
    }
    if (range.max == null) {
      return `${formatMoney(range.min)}〜`;
    }
    return `${formatMoney(range.min)}〜${formatMoney(range.max)}`;
  }

  function resolveLegacyUrl(path) {
    if (!path) {
      return '';
    }
    if (/^https?:\/\//i.test(path)) {
      return path;
    }
    const normalizedPath = path.charAt(0) === '/' ? path : `/${path}`;
    if (normalizedPath.startsWith('/products/') || normalizedPath.startsWith('/collections/')) {
      return normalizedPath;
    }
    if (!CONFIG.legacyHost) {
      return normalizedPath;
    }
    return `${CONFIG.legacyHost}${normalizedPath}`;
  }

  function normalizeItem(raw) {
    const discountRate = Number(raw.discount_rate) || 0;
    const bargainPrice = raw.bargain_price === '' || raw.bargain_price == null ? null : Number(raw.bargain_price);
    const salePrice = raw.sale_price === '' || raw.sale_price == null ? null : Number(raw.sale_price);
    // セール判定は sale_flg ではなく discount_rate>0。実売=bargain_price / 取消線=sale_price。
    const onSale = discountRate > 0 && bargainPrice != null;
    const imageUrls = (Array.isArray(raw.item_img_url_all) ? raw.item_img_url_all : [])
      .map(resolveLegacyUrl)
      .filter(Boolean);
    const imageUrl = resolveLegacyUrl(raw.item_img_url) || imageUrls[0] || '';

    const badges = [];
    if (Number(raw.new_flg) === 1) {
      badges.push({ key: 'new', label: 'NEW' });
    }
    if (Number(raw.reserve_flg) === 1) {
      badges.push({ key: 'reserve', label: 'PRE-ORDER' });
    }
    if (raw.restock_flg !== '' && raw.restock_flg != null && Number(raw.restock_flg) !== 0) {
      badges.push({ key: 'restock', label: 'RESTOCK' });
    }
    if (onSale) {
      badges.push({ key: 'sale', label: `${discountRate}%OFF` });
    }
    if (Number(raw.item_stock_cnt) <= 0) {
      badges.push({ key: 'soldout', label: 'SOLD OUT' });
    }

    return {
      id: raw.item_id,
      idColorCd: raw.item_id_color_cd,
      colorCode: raw.color_cd || '',
      sku: raw.sku_cd || '',
      title: raw.item_nm || '',
      brand: raw.brand_nm || '',
      colorName: raw.color_nm || raw.color_base_nm || '',
      colorPosition: raw.color_position !== '' && raw.color_position != null && Number.isFinite(Number(raw.color_position)) ? Number(raw.color_position) : null,
      sizeName: raw.size_nm || '',
      price: onSale ? bargainPrice : salePrice,
      compareAtPrice: onSale ? salePrice : null,
      discountRate: discountRate,
      onSale: onSale,
      soldOut: Number(raw.item_stock_cnt) <= 0,
      imageUrl: imageUrl,
      imageLargeUrl: resolveLegacyUrl(raw.item_img_large_url),
      imageUrls: imageUrls,
      // ZETAのdesc_urlはdev・本番ともShopify商品URLに使えないため、item_idから組み立てる。
      url: raw.item_id ? `/products/${encodeURIComponent(String(raw.item_id).toLowerCase())}` : '',
      badges: badges,
    };
  }

  // stock と color_info は ZETA で必須のため常に付与する。
  function buildItemQuery(params) {
    const search = new URLSearchParams();
    search.set('q', params.q || '');
    search.set('l', String(params.l != null ? params.l : CONFIG.perPage));
    search.set('o', String(params.o || 0));
    // m を送るとフィールドが絞られ item_img_url_all 等が返らないため指定しない。
    search.set('s', params.s || '-score');
    search.set('stock', String(params.stock != null ? params.stock : 0));
    search.set('color_info', '0');
    search.set('c', FACET_TYPES.join(','));
    search.set('f', '0');

    const filters = params.filters || {};
    Object.keys(filters).forEach((field) => {
      const values = filters[field];
      if (Array.isArray(values) && values.length > 0) {
        search.set(field, values.join(','));
      }
    });

    return `?${search.toString()}`;
  }

  async function request(url) {
    const response = await fetch(url, { mode: 'cors' });
    if (!response.ok) {
      const text = await response.text();
      throw new Error(`ZETA API ${response.status}: ${text.slice(0, 200)}`);
    }
    return response.json();
  }

  async function fetchItems(params = {}) {
    const json = await request(`${CONFIG.apiBase}/item${buildItemQuery(params)}`);
    const result = json.result || {};
    const attribute = result.attribute || {};
    return {
      total: Number(attribute.total) || 0,
      count: Number(attribute.count) || 0,
      offset: Number(attribute.offset) || 0,
      items: (result.items || []).map(normalizeItem),
      facets: result.facet || {},
    };
  }

  async function fetchSuggest(qWord, options = {}) {
    const search = new URLSearchParams();
    search.set('q_word', qWord || '');
    search.set('suggest_limit', String(options.suggestLimit != null ? options.suggestLimit : 10));
    search.set('popularword_limit', String(options.popularLimit != null ? options.popularLimit : 10));
    search.set('story_limit', '0');

    const json = await request(`${CONFIG.apiBase}/suggest?${search.toString()}`);
    return {
      suggestions: Array.isArray(json.suggestion) ? json.suggestion : [],
      popularWords: Array.isArray(json.popularwords) ? json.popularwords : [],
    };
  }

  function buildResultsUrl(keyword) {
    const search = new URLSearchParams();
    search.set('q', keyword || '');
    search.set('view', 'zeta');
    return `${CONFIG.searchUrl}?${search.toString()}`;
  }

  window.Zeta = {
    config: CONFIG,
    facetTypes: FACET_TYPES,
    colorSwatchMap: COLOR_SWATCH_MAP,
    priceRanges: PRICE_RANGES,
    formatMoney,
    getPriceRangeFacetName,
    resolvePriceRange,
    formatPriceRange,
    resolveLegacyUrl,
    resolveImageCutKey,
    findDifferentCutImageUrl,
    hydrateProductCards,
    repairProductImages,
    normalizeItem,
    buildItemQuery,
    fetchItems,
    fetchSuggest,
    buildResultsUrl,
  };
})();
